import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  buyersApi,
  type BuyerInterviewDto,
  type OverrideBuyerInterviewVerdictPayload,
  type SaveBuyerInterviewAnswerPayload,
} from "@/api/buyers";
import { buyerKeys } from "@/hooks/buyers/buyerKeys";
import { isNotFoundError } from "../queryStates";

/**
 * Reads and writes for the qualification interview.
 *
 * <p>🔴 <b>The autosave writes through `setQueryData`, never an
 * invalidation.</b> The PUT returns the whole interview, so the response IS the
 * new state — invalidating instead would fire a refetch per recorded answer, on
 * a surface that writes a twenty-question spine plus branches plus debounced
 * verbatim text during a live phone call. `staleAfterSaveGate` accepts
 * `setQueryData` for exactly this shape.</p>
 *
 * <p>The three writes that MOVE THE RECORD — start, complete, override —
 * invalidate `buyerKeys.all` on top of that, because completing projects onto
 * `BuyerLeadQualification` and can stamp `qualifiedAtUtc`: the buyer's own
 * detail read genuinely changes, and the Qualification tab reads both.</p>
 */

/** How long the CURRENT wording is trusted before a read refetches it. Short,
 *  because an admin can publish new wording at any time; a publish also
 *  invalidates `buyerKeys.interviewRegistryAll()`. */
const CURRENT_REGISTRY_STALE_MS = 60_000;

/**
 * The served question registry, in one wording version.
 *
 * <p>Buyer-agnostic, so it is cached once for the app rather than per buyer,
 * and never invalidated by a buyer write. The wording is admin-editable and
 * versioned: with `wordingVersion` (0 = the coded defaults) it is that
 * published version, which never changes once published, so it is cached
 * forever. Without it, it is the wording in force NOW — what a live interview
 * shows and will be recorded with — which a publish can change at any moment,
 * so it goes stale quickly and stays refetchable.</p>
 */
export function useInterviewRegistry(wordingVersion?: number, enabled = true) {
  return useQuery({
    queryKey: buyerKeys.interviewRegistry(wordingVersion),
    queryFn: () => buyersApi.getInterviewRegistry(wordingVersion),
    enabled,
    // A numbered version is immutable — but when the store cannot be read the
    // server answers with the coded defaults (version 0) instead, and that
    // fallback must not be cached as version N for the rest of the session.
    staleTime: (query) =>
      wordingVersion === undefined
        ? CURRENT_REGISTRY_STALE_MS
        : (query.state.data?.wordingVersion ?? 0) === wordingVersion
          ? Infinity
          : 0,
  });
}

/** The wording version a completed interview's read-back renders in. A
 *  completed interview always carries one (0 for one completed before the
 *  wording editor); null or absent reads as 0, the coded defaults. */
export function recordedWordingVersion(interview: { wordingVersion?: number | null }): number {
  return interview.wordingVersion ?? 0;
}

/**
 * The interview in progress, else the newest completed one.
 *
 * <p>🔴 <b>A 404 is resolved to `null`, not raised.</b> "No interview has been
 * conducted" is the normal state of an unassessed buyer, and rendering it
 * through the failed-request path would tell a specialist their request broke
 * when the server answered perfectly well. Every other failure still surfaces
 * as an error, so a real outage is never mistaken for an unassessed buyer.</p>
 */
export function useBuyerInterview(buyerLeadId: string, enabled = true) {
  return useQuery({
    queryKey: buyerKeys.interview(buyerLeadId),
    queryFn: async (): Promise<BuyerInterviewDto | null> => {
      try {
        return await buyersApi.getInterview(buyerLeadId);
      } catch (error) {
        if (isNotFoundError(error)) return null;
        throw error;
      }
    },
    enabled,
  });
}

/** Every interview conducted for this buyer, newest first. */
export function useBuyerInterviewVersions(buyerLeadId: string, enabled = true) {
  return useQuery({
    queryKey: buyerKeys.interviewVersions(buyerLeadId),
    queryFn: () => buyersApi.listInterviews(buyerLeadId),
    enabled,
  });
}

/** One prior version, read-only. */
export function useBuyerInterviewVersion(
  buyerLeadId: string,
  version: number | null,
) {
  return useQuery({
    queryKey: buyerKeys.interviewVersion(buyerLeadId, version ?? -1),
    queryFn: () => buyersApi.getInterviewVersion(buyerLeadId, version as number),
    enabled: version != null,
  });
}

/** Start one, or resume the one in progress. Idempotent server-side. */
export function useStartInterview(buyerLeadId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => buyersApi.startInterview(buyerLeadId),
    onSuccess: (row) => {
      qc.setQueryData(buyerKeys.interview(buyerLeadId), row);
      void qc.invalidateQueries({ queryKey: buyerKeys.all });
    },
  });
}

/** One autosaved answer. `seq` rides the variables so an out-of-order response
 *  cannot overwrite a newer one — see `applyIfLatest` at the call site. */
export interface SaveAnswerVars extends SaveBuyerInterviewAnswerPayload {
  seq: number;
}

export function useSaveInterviewAnswer(
  buyerLeadId: string,
  applyIfLatest: (seq: number, row: BuyerInterviewDto) => boolean,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ seq: _seq, ...body }: SaveAnswerVars) =>
      buyersApi.saveInterviewAnswer(buyerLeadId, body),
    onSuccess: (row, vars) => {
      // The response is the whole interview, so this IS the fresh state — and
      // only the newest in-flight save may install it, or a slow early PUT
      // lands after a fast later one and the coverage counts go backwards.
      if (applyIfLatest(vars.seq, row)) {
        qc.setQueryData(buyerKeys.interview(buyerLeadId), row);
      }
    },
  });
}

/** Complete it and reveal the verdicts. */
export function useCompleteInterview(buyerLeadId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => buyersApi.completeInterview(buyerLeadId),
    onSuccess: (row) => {
      qc.setQueryData(buyerKeys.interview(buyerLeadId), row);
      // Completion projects onto the standing assessment and can stamp the
      // `qualifiedAtUtc` milestone, so the buyer's detail read moves too.
      void qc.invalidateQueries({ queryKey: buyerKeys.all });
    },
  });
}

/** Override the computed roll-up on a completed interview. */
export function useOverrideInterviewVerdict(buyerLeadId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: OverrideBuyerInterviewVerdictPayload) =>
      buyersApi.overrideInterviewVerdict(buyerLeadId, body),
    onSuccess: (row) => {
      qc.setQueryData(buyerKeys.interview(buyerLeadId), row);
      void qc.invalidateQueries({ queryKey: buyerKeys.all });
    },
  });
}
